import { useState, useEffect, useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === "development" ? `http://${window.location.hostname}:8000` : "");

export default function StickyStockSearch({ selectedCompany, onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 1) {
      setResults([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await fetch(`${API_URL}/search-tickers?q=${encodeURIComponent(query)}`);
        if (resp.ok) {
          const data = await resp.json();
          setResults(data.companies || []);
          setOpen(true);
        }
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handlePick = (company) => {
    onSelect?.(company);
    setQuery("");
    setOpen(false);
    setFocused(false);
    inputRef.current?.blur();
  };

  const displayValue = focused
    ? query
    : selectedCompany?.name
      ? selectedCompany.name.toUpperCase()
      : "";

  return (
    <div ref={containerRef} className="sticky top-12 z-40 bg-gray-950/95 backdrop-blur-md py-2">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => { setQuery(e.target.value); }}
          onFocus={() => { setFocused(true); setQuery(""); }}
          placeholder={selectedCompany ? "Search another stock…" : "Search ticker or company…"}
          className="w-full bg-gray-900 text-white font-medium text-sm px-4 py-2.5 rounded-xl border border-blue-500/60 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 placeholder:text-gray-500 placeholder:font-normal placeholder:text-xs"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
        />
        {loading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">…</div>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl max-h-80 overflow-auto z-50">
          {results.map((c) => (
            <button
              key={`${c.cik}-${c.ticker}`}
              type="button"
              onClick={() => handlePick(c)}
              className="block w-full text-left px-4 py-3 hover:bg-gray-800 active:bg-gray-800 border-b border-gray-800 last:border-b-0"
            >
              <div className="flex justify-between items-baseline">
                <span className="text-white font-bold text-sm">{c.ticker}</span>
                <span className="text-gray-500 text-[10px] uppercase tracking-wide">{c.industry || ''}</span>
              </div>
              <div className="text-gray-300 text-xs mt-0.5 truncate">{c.name}</div>
            </button>
          ))}
        </div>
      )}

      {open && !loading && query && results.length === 0 && (
        <div className="absolute left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl px-4 py-3 text-gray-400 text-sm">
          No matches for "{query}"
        </div>
      )}
    </div>
  );
}
